/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import dayjs from "dayjs";
import { AnimatePresence, motion } from "framer-motion";
import { nanoid } from "nanoid";
import { useRouter } from "next/router";
import { useState } from "react";
import ReactTooltip from "react-tooltip";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { ContactFilterAtom } from "../../../atoms/ContactFilterAtom";
import { FilterCounterAtom } from "../../../atoms/ContactFilterAtoms";
import { SelectedTagsAtom } from "../../../atoms/SelectedTags";
import { ICategoryType } from "../../../generated/Api";
import useMediaQuery from "../../../hook/useMediaQuery";
import { customTooltip } from "../../../styles/general";
import { IOptionValues } from "../../../types/TagQueryTypes";
import { ITag, ITagUsedIn } from "../../../types/TagTypes";
import useDebounce from "../../../utils/UseDebounce";
import LoadingAbsolute from "../../LoadingAbsolute";
import StyledCheckBox from "../../StyledCheckBox";
import Tag from "../../Tag";
import CampaignListPopover from "./BatchDelete/CampaignListPopover";
import MoreActionsPopOver from "./TagActions";

interface TableProps {
  check: boolean;
}

const Row = styled(motion.div)<TableProps>`
  display: grid;
  border: 1px solid;
  border-color: ${(props) => (props.check ? "#FB972E" : "#eaeaeabf")};
  background-color: ${(props) =>
    props.check ? "rgb(251, 151, 46, 0.05)" : "#ffffff"};
  grid-template-rows: 1fr;
  align-items: center;
  min-width: 100%;
  width: max-content;
`;

const GridRow = styled(motion.div)`
  min-width: 100%;
  width: max-content;
  height: 60px;

  background-color: #ffffff;

  display: grid;
  position: relative;

  /* grid-template-columns:
    100px minmax(120px, 160px)
    240px 120px minmax(110px, 180px) 60px; */

  grid-template-columns:
    100px 140px 140px 120px repeat(4, 160px) 180px
    60px;

  grid-column-gap: 24px;

  padding: 15px 25px;

  align-items: center;
  justify-content: space-between;

  @media screen and (max-width: 1010px) {
    width: fit-content;
  }
`;

const TableTitle = styled.div`
  color: #2f2f2f;
  display: flex;
  font-size: 16px;
  align-items: center;
  /* overflow: hidden; */
  max-width: 100%;
`;

const TableActionsRow = styled.div`
  color: #2f2f2f;
  display: flex;
  font-size: 14px;
  align-items: center;
  justify-content: flex-end;
  margin-right: 10px;
  cursor: pointer;
`;

const marginRight30 = css`
  margin-right: 30px;
`;

const TextCss = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
`;

const userCss = css`
  ${TextCss}
  cursor: pointer;
  :hover {
    opacity: 0.6;
  }
`;

const CampaignsUsedTag = styled.div`
  width: fit-content;
  max-width: 100%;
  background-color: #f2f2f2;
  color: #2f2f2f;
  position: relative;

  border-radius: 4px;
  max-height: 32px;
  padding: 6px 11px;
  font-size: 15px;
  margin: 0px 4px;

  &:first-child {
    margin-left: 0px;
  }
`;

const TextContainer = styled.div`
  text-overflow: ellipsis;
  max-width: 85px;
  overflow: hidden;
  white-space: nowrap;
  /* cursor: default; */
`;

const ContainerCampaignsUsedTag = styled.div`
  display: flex;
`;

const WrapperCheckboxAndTitle = styled.div`
  display: flex;
  align-content: center;
  justify-content: start;
`;

const TooltipText = styled.span`
  font-size: 14px;
  letter-spacing: 0px;
  color: #fff;
  text-align: center;
`;

const TooltipTextMore = styled.span`
  font-size: 14px;
  letter-spacing: 0px;
  color: #333;
  text-align: center;
`;

interface Props {
  row: ITag;
  isChecked: boolean;
  handleCheck: () => void;
  campaignsUsedIn: Array<{
    id: string;
    name: string;
    type: ITagUsedIn;
  }>;
}

export function ucfirst(str: string) {
  var firstLetter = str.slice(0, 1);
  return firstLetter.toUpperCase() + str.substring(1).toLowerCase();
}

export default function TableRow(props: Props): JSX.Element {
  const { row, handleCheck, isChecked, campaignsUsedIn } = props;
  const setFilterCounter = useSetRecoilState(FilterCounterAtom);
  const isLargerThan1200 = useMediaQuery("(min-width: 1200px)");
  const setContactFilter = useSetRecoilState(ContactFilterAtom);
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const selectedTags = useRecoilValue(SelectedTagsAtom);
  const [isOpenDropView, setIsOpenDropView] = useState(false);
  const isOpenDropViewDebounced = useDebounce(isOpenDropView, 200);

  const tagsUsedInStartTag = campaignsUsedIn.filter(
    (c) => c.type === "start-tag"
  );
  const tagsUsedInStopTag = campaignsUsedIn.filter(
    (c) => c.type === "stop-tag"
  );
  const tagsUsedInNodeTag = campaignsUsedIn.filter(
    (c) => c.type === "node-tag"
  );
  const tagsUsedInForm = campaignsUsedIn.filter((c) => c.type === "form");

  async function filterContact() {
    if (isLoading) return;
    setIsLoading(true);
    const tagQuery = {
      operator: "ANY_OF" as IOptionValues,
      tags: [row._id || ""],
      next: null,
    };

    // Set a placeholder filter for it to be cleaned
    setContactFilter({
      withTags: [row],
      withoutTag: [],
      allOfTags: [],
      tagQuery: tagQuery,
      recipientTags: [
        {
          option: {
            name: "With any of these tags",
            value: "ANY_OF",
          },
          logic: { value: "AND", name: "And" },
          tags: [row],
          id: nanoid(),
        },
      ],
    });

    setFilterCounter({
      byLabelCount: 0,
      byStatusCount: 0,
      byTagsCount: 1,
      bySavedSearchCount: 0,
      byInteractionCount: 0,
    });

    router.push("/contacts?fromTag=true");

    setIsLoading(false);
  }

  const TagColumn = (
    tagsUsedIn: Array<{
      id: string;
      name: string;
      type: ITagUsedIn;
    }>,
    isForm?: boolean
  ) => {
    const isEmptyColumn = !tagsUsedIn.length;

    if (isEmptyColumn)
      return (
        <TableTitle>
          <ContainerCampaignsUsedTag>-</ContainerCampaignsUsedTag>
        </TableTitle>
      );

    return (
      <TableTitle>
        <ContainerCampaignsUsedTag>
          {tagsUsedIn.length === 1 ? (
            <CampaignsUsedTag
              data-tip
              data-for={tagsUsedIn[0].id}
              id="tag-container"
            >
              <TextContainer>{tagsUsedIn[0].name}</TextContainer>

              <ReactTooltip
                id={tagsUsedIn[0].id}
                place="top"
                effect="solid"
                backgroundColor="#333333"
                css={customTooltip}
              >
                <TooltipText>{tagsUsedIn[0].name}</TooltipText>
              </ReactTooltip>
            </CampaignsUsedTag>
          ) : (
            <CampaignsUsedTag
              onMouseEnter={() => setIsOpenDropView(true)}
              onMouseLeave={() => setIsOpenDropView(false)}
            >
              <TextContainer>{`${tagsUsedIn.length} ${
                isForm ? "form" : "campaign"
              }${tagsUsedIn.length > 1 && "s"}`}</TextContainer>

              {/* Popover with all campaigns used with this tag */}
              <CampaignListPopover
                tagList={tagsUsedIn}
                type={tagsUsedIn[0].type}
                isOpen={isOpenDropViewDebounced as boolean}
                handleClose={() => setIsOpenDropView(false)}
              />
            </CampaignsUsedTag>
          )}
        </ContainerCampaignsUsedTag>
      </TableTitle>
    );
  };

  return (
    <Row
      check={isChecked}
      layout
      css={css`
        margin-top: 7px;
      `}
    >
      <AnimatePresence>
        <GridRow layout>
          <WrapperCheckboxAndTitle>
            <StyledCheckBox
              customCss={css`
                margin-right: 10px;
              `}
              check={isChecked}
              handleCheck={handleCheck}
            />
            <TableTitle>{row.type}</TableTitle>
          </WrapperCheckboxAndTitle>
          <TableTitle css={TextCss}>{row.category}</TableTitle>

          <TableTitle css={TextCss}>
            <Tag
              customStyle={{ margin: "10px 0px", maxWidth: "100%" }}
              tag={{
                categoryId: "",
                createdAt: new Date(),
                name: row.name,
                userUid: "",
                type:
                  row.type === "system"
                    ? ICategoryType.System
                    : ICategoryType.User,
                category: row.category,
                _id: row._id,
                isStartTag: !!tagsUsedInStartTag.length,
                isStopTag: !!tagsUsedInStopTag.length,
              }}
            />
          </TableTitle>

          <TableTitle onClick={() => filterContact()} css={userCss}>
            {row.contactCount || 0}
          </TableTitle>

          {TagColumn(tagsUsedInNodeTag)}
          {TagColumn(tagsUsedInStartTag)}
          {TagColumn(tagsUsedInStopTag)}
          {TagColumn(tagsUsedInForm, true)}

          <TableTitle>
            {isLargerThan1200
              ? dayjs(row.createdAt).format("MMM D, YYYY [@] h:mm a")
              : dayjs(row.createdAt).format("MM/DD/YYYY")}
          </TableTitle>

          <TableActionsRow>
            {selectedTags.length <= 1 && row.type !== "system" && (
              <MoreActionsPopOver tag={row} />
            )}
          </TableActionsRow>
        </GridRow>
      </AnimatePresence>
      {isLoading && <LoadingAbsolute />}
    </Row>
  );
}
